// Function index:
//   runGame(user, partyId) — boot the game screen for one party. Fetches
//       bootstrap, runs the character picker if needed, mounts the
//       party-scoped surface (chat / banner / WS / journal / strategic
//       map) once, then mounts the world via mount-world.ts and
//       re-mounts on every character.world.changed event so interiors
//       can swap in/out cleanly.
//   initialBootstrap(partyId, chat) — private helper that runs the
//       character picker on first connect if the party has no character.

import { mountChat, type ChatMount, type PresenceUser } from '../chat.js';
import { mountCombatBanner } from '../combat-banner.js';
import { mountRulesPauseBanner } from '../rules-pause-banner.js';
import { mountDiceRoller } from '../dice-roller.js';
import { mountEncounterWindow } from '../encounter/encounter-window.js';
import { archetypeOf } from './archetypes.js';
import { mountPartyRail } from './party-rail.js';
import { mountHud } from './hud.js';
import { connectWS } from '../ws.js';
import {
  fetchBootstrap, logout,
  type AuthUser, type BootstrapResponse,
} from '../api.js';
import { showCharacterScreen } from '../screens/character.js';
import { mountCharacterSheet } from '../screens/character-sheet.js';
import { mountStrategicMap } from '../strategic-map.js';
import { mountJournalPanel } from './journal.js';
import { mountLedgerPanel } from './ledger-panel.js';
import { mountChroniclePanel, setRecapEntries } from './chronicle-panel.js';
import { mountHexHistoryOverlay } from './hex-history-overlay.js';
import { mountQuestBoard } from './quest-board.js';
import { mountQuestLog } from './quest-log.js';
import { mountShopPanel } from './shop.js';
import { mountWorld } from './mount-world.js';

export async function runGame(user: AuthUser, partyId: number): Promise<void> {
  document.getElementById('screen-game')!.hidden = false;
  const appEl = document.getElementById('app')!;
  const who = document.getElementById('who')!;
  const partyLabel = document.getElementById('party-label')!;
  const selfHp = document.getElementById('self-hp') as HTMLSpanElement;
  const logoutBtn = document.getElementById('leave-btn') as HTMLButtonElement;

  who.textContent = user.display_name;
  logoutBtn.addEventListener('click', async () => { await logout(); location.search = ''; });

  mountJournalPanel(partyId);

  const chat = mountChat(null, user.id);
  const banner = mountCombatBanner();
  const pauseBanner = mountRulesPauseBanner();

  let boot = await initialBootstrap(partyId, chat);

  partyLabel.textContent = `${boot.party.name} · ${boot.world.name} (${boot.world.setting})`;
  // Rehydrate the narrative log from the bootstrap slice so a refresh (or
  // reconnect, below) resumes the story instead of blanking it. The server
  // keeps the full transcript; this is the read path.
  const renderHistory = (): void => chat.history(boot.messages.map((m) => ({
    role: m.role,
    self: m.author_id === user.id,
    name: m.display_name,
    content: m.content,
  })));
  renderHistory();
  // Keep the narrative log clean for the GM's opening scene — only surface the
  // genuinely-wrong case (no character) rather than "connecting…/playing X".
  if (!boot.character) chat.system('no character assigned to this party');

  const selfCharId = boot.character?.id ?? null;
  const charNames = new Map<number, string>(boot.party_characters.map((c) => [c.id, c.name]));
  if (boot.character) charNames.set(boot.character.id, boot.character.name);
  // NPC names for hp/combat lines. Seeded from bootstrap (covers reconnecting
  // into a live fight, where combat.started already happened) and kept fresh
  // by ws-bindings as fights start.
  const npcNames = new Map<number, string>(boot.npcs.map((n) => [n.id, n.name]));
  for (const p of boot.combat?.participants ?? []) {
    if (p.kind === 'npc' && p.npc_id !== null) npcNames.set(p.npc_id, p.name);
  }

  const renderSelfHp = (hp: number, max_hp: number): void => {
    selfHp.textContent = `HP ${hp}/${max_hp}`;
    selfHp.classList.toggle('low', hp <= max_hp * 0.33 || hp === 0);
  };
  if (boot.character) renderSelfHp(boot.character.hp, boot.character.max_hp);

  // Left-hand player rail + right-hand HUD (actions tray + options gear).
  // Both are party-scoped: mounted once, alive across world transitions.
  const rail = mountPartyRail({ selfCharId, self: boot.character, members: boot.party_characters, inviteCode: boot.party.invite_code });
  mountHud();

  const updateInputGate = (): void => {
    const active = banner.active();
    if (!active) { chat.setInputEnabled(true); return; }
    const isMine = active.kind === 'character' && active.character_id === selfCharId;
    chat.setInputEnabled(isMine, isMine ? undefined : `waiting for ${active.name}…`);
  };
  banner.setCombat(boot.combat);
  pauseBanner.setPaused(boot.party.rules_pause_topic);
  updateInputGate();

  const wsProto = location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = connectWS(`${wsProto}://${location.host}/ws?party=${partyId}`);
  chat.attachWS(ws);
  // Keep the player rail live. These are additive to the per-world bindings
  // in ws-bindings.ts and persist across world transitions, so they're wired
  // here on the party-scoped socket rather than inside mount-world.
  ws.on('character.hp_changed', (m: { character_id: number; hp: number; max_hp: number }) => rail.updateHp(m.character_id, m.hp, m.max_hp));
  ws.on('character.leveled', (m: { character_id: number; hp: number; max_hp: number }) => rail.updateHp(m.character_id, m.hp, m.max_hp));
  ws.on('presence.update', (m: { users: PresenceUser[] }) => rail.setPresence(m.users));
  // Chronicle recap is party-scoped, not world-scoped — register it here,
  // immediately on connect, so it can never lose the race against
  // mountHexWorld (which used to be the sole registrant, via ws-bindings.ts).
  ws.on('chronicle.recap', (msg: { entries: Array<{ kind: string; text: string; mine: boolean; created_at: string }> }) => {
    setRecapEntries(msg.entries);
  });
  // Table-talk is party-scoped like the recap: registered on connect so a
  // slow world mount can never drop a member's line. Own messages are skipped
  // inside playerSaid — the local echo on submit already rendered them.
  ws.on('player.said', (msg: { user_id: number; display_name: string; content: string }) => {
    chat.playerSaid(msg.user_id, msg.display_name, msg.content);
  });
  // Policy close: the session is gone (signed out elsewhere, removed from the
  // party). No reconnect will ever succeed — say so instead of silence.
  ws.on('ws.dead', () => {
    chat.system('your session has ended — you may have been signed out or removed from this party. Reload the page to sign in again.');
    chat.setInputEnabled(false, 'session ended — reload the page');
  });
  // Dice roller is party-scoped (like chat) and persists across world
  // transitions; mount once and reuse the live socket.
  mountDiceRoller(ws);
  // Rest (defeat-recovery loop): server-resolved, zero model calls. Outcome
  // arrives as hp_changed + a system line, so the button needs no local state.
  document.getElementById('rest-btn')?.addEventListener('click', () => { ws.send('player.rest', {}); });
  // Strategic map (with click-to-fast-travel once the socket is live).
  mountStrategicMap(partyId, ws, pauseBanner.isPaused);
  // Hex history overlay — party-scoped like the map/journal; the per-world
  // hex mount registers the right-click handler that opens it.
  const hexHistory = mountHexHistoryOverlay();
  // NPC relationship ledger — party-scoped like the map/journal; refetches
  // people/factions/places fresh on every open.
  mountLedgerPanel(partyId);
  // World + party chronicle — party-scoped like the ledger; primes its
  // first open from a chronicle.recap push (registered above, immediately
  // on connect, so a slow world mount can't lose the race and drop it).
  mountChroniclePanel(partyId);
  // Character sheet + inventory + reputation panel (only when a character is bound).
  if (boot.character) mountCharacterSheet({ character: boot.character, partyId, ws });
  // Quest log is party-scoped; mount once with the live socket.
  mountQuestLog(partyId, ws);
  mountQuestBoard(partyId);
  // Trade panel — party-scoped like the board; refreshes on coin/inventory
  // changes over the same live socket.
  mountShopPanel({ partyId, selfCharId, ws });
  // Encounter window — party-scoped like the combat banner, registered
  // immediately on connect (combat.started can fire the moment the GM's
  // tool loop runs; the ws wrapper supports multiple handlers per type, so
  // the banner's own combat bindings are unaffected). boot.combat re-opens
  // the window on reconnect mid-fight. The archetype is derived once here
  // (it's fixed for the character's lifetime, like the class it comes from)
  // and feeds the action bar's sixth signature button.
  const encounter = mountEncounterWindow(
    ws, boot.combat, selfCharId,
    boot.character ? archetypeOf(boot.character.character_class) : null,
    // The action bar's free-text last option routes through chat's own
    // send+echo path, so the typed line lands in the narrative log and the
    // server adjudicates it exactly like a message typed in chat.
    (text) => chat.say(text),
  );

  // The chat / WS / combat banner are party-scoped and persist across
  // world transitions. Only the scene + per-world UI rebuilds.
  let teardownWorld: (() => void) | null = null;
  const mount = (): void => {
    teardownWorld = mountWorld({
      appEl, partyId, boot, ws, chat, banner, pauseBanner, isPaused: pauseBanner.isPaused, selfCharId,
      charNames, npcNames, onSelfHpChange: renderSelfHp, onCombatChange: updateInputGate,
      partyLabel, hexHistory,
    });
  };
  mount();

  // NM-D3: world transitions tear down the current scene + per-world UI
  // and remount from a fresh bootstrap. Only fires for the speaker's own
  // character — other party members crossing world boundaries are
  // handled inside mount-world.ts via add/removeCharacter on the
  // current scene.
  ws.on('character.world.changed', async (msg: { character_id: number }) => {
    if (msg.character_id !== selfCharId) return;
    chat.system('crossing into a new world…');
    teardownWorld?.();
    try {
      boot = await fetchBootstrap(partyId);
    } catch (err) {
      chat.system(`world refresh failed: ${(err as Error).message}`);
      return;
    }
    pauseBanner.setPaused(boot.party.rules_pause_topic);
    partyLabel.textContent = `${boot.party.name} · ${boot.world.name} (${boot.world.setting})`;
    mount();
  });

  // Every event during a drop is gone (there is no server-side replay), so a
  // reconnect re-syncs from a fresh bootstrap: the narrative log is re-rendered
  // from the server's transcript, the combat banner + input gate reflect the
  // real fight state (a missed combat.ended otherwise wedges the input on
  // "waiting for X…" forever), and the scene remounts like a world change.
  ws.onReconnect(async () => {
    try {
      boot = await fetchBootstrap(partyId);
    } catch (err) {
      chat.system(`reconnected, but re-sync failed: ${(err as Error).message} — try refreshing`);
      return;
    }
    renderHistory();
    chat.system('connection restored — the table is caught up');
    partyLabel.textContent = `${boot.party.name} · ${boot.world.name} (${boot.world.setting})`;
    pauseBanner.setPaused(boot.party.rules_pause_topic);
    banner.setCombat(boot.combat);
    // The encounter window mirrors the banner: a combat.started (or .ended)
    // lost during the drop otherwise leaves a live fight with no fighting
    // screen — or a dead fight with one wedged open.
    encounter.sync(boot.combat);
    updateInputGate();
    if (boot.character) renderSelfHp(boot.character.hp, boot.character.max_hp);
    teardownWorld?.();
    mount();
  });
}

async function initialBootstrap(partyId: number, chat: ChatMount): Promise<BootstrapResponse> {
  let boot: BootstrapResponse;
  try { boot = await fetchBootstrap(partyId); }
  catch (err) { chat.system(`bootstrap failed: ${(err as Error).message}`); throw err; }

  // Need a character? Run the picker/create flow, then re-bootstrap.
  if (!boot.character) {
    document.getElementById('screen-game')!.hidden = true;
    await showCharacterScreen({
      worldId: boot.world.id,
      worldName: boot.world.name,
      partyId: boot.party.id,
      partyName: boot.party.name,
    });
    document.getElementById('screen-game')!.hidden = false;
    boot = await fetchBootstrap(partyId);
  }
  return boot;
}
